import * as Yup from 'yup';

// Functions

/**
 * Returns a concatanated Yup object out of infinite number Yup objects
 * @param  {object} Yup object schemas
 * @returns {object} Concatanated Yup object
 */

const merge = (...schemas) => {
  const [first, ...rest] = schemas;

  const merged = rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first);

  return merged;
};

/* -------------------- CONSTANTS ------------------------------ */
const requiredText = 'Lütfen boş bırakmayınız!';
const passwordLengthText = 'Parola en az 5 en fazla 12 karakterden oluşmalıdır';
const passwordText = 'Parola en az 1 büyük, 1 küçük, 1 özel karakter ile 1 rakam içermelidir';
const passwordRegex =
  /^((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*["'+\-(),;./<=>?_:~!{|}[\]@#$%^&*]).*$/;
const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const phoneRegex =
  /^\+([0-9]{2,3}) \(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{2})[-. ]?([0-9]{2})$/;

const requiredString = Yup.string().strict(false).trim().required(requiredText);
const passwordSchemaLocal = Yup.string()
  .required(requiredText)
  .min(5, passwordLengthText)
  .max(12, passwordLengthText)
  .matches(passwordRegex, passwordText);

// PASSWORD SCHEMA
export const passwordSchema = Yup.object({
  password: passwordSchemaLocal,
  password_confirmation: Yup.string()
    .required(requiredText)
    .min(5, passwordLengthText)
    .max(12, passwordLengthText)
    .when('password', {
      is: val => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], 'Parola eşleşmiyor!')
    })
});

// REQUIRED EMAIL SCHEMA
export const requiredEmailSchema = Yup.object({
  email: Yup.string()
    .strict(false)
    .trim()
    .required(requiredText)
    .matches(emailRegex, 'Lütfen geçerli bir e-posta girin')
});

// OPTIONAL EMAIL SCHEMA
export const optionalEmailSchema = Yup.object({
  email: Yup.string()
    .strict(false)
    .trim()
    .optional()
    .nullable()
    .matches(emailRegex, 'Lütfen geçerli bir e-posta girin')
});

// REQUIRED PHONE SCHEMA
export const phoneSchema = Yup.object({
  telephone: Yup.string()
    .required(requiredText)
    .matches(phoneRegex, 'Telefon numarasını lütfen doğru girin')
});

// TCKN - Custom method
Yup.addMethod(Yup.string, 'tckn', function (errorMessage) {
  return this.test(`test-tckn`, errorMessage, function (value) {
    const { path, createError } = this;
    const isEleven = /^[0-9]{11}$/.test(value);

    if (isEleven) {
      let totalFirstTen = 0,
        totalOdds = 0,
        totalEvens = 0;

      for (let i = 0; i < 10; i++) {
        totalFirstTen += Number(value?.substr(i, 1));
      }

      const rule1 = totalFirstTen % 10 === Number(value?.substr(10, 1));

      if (rule1) {
        for (let i = 0; i < 10; i += 2) {
          totalEvens += Number(value?.substr(i, 1));
        }

        for (let i = 1; i < 10; i += 2) {
          totalOdds += Number(value?.substr(i, 1));
        }

        const rule2 = (totalEvens * 7 - totalOdds) % 10 === Number(value?.substr(9, 0));

        return rule2 ? true : createError({ path, message: errorMessage });
      } else {
        return createError({ path, message: errorMessage });
      }
    } else return true;
  });
});

// TCKN Schema
const tcknSchema = Yup.object({
  tckn: Yup.string()
    .strict(false)
    .trim()
    .required(requiredText)
    .min(11, 'TCKN en az 11 karakter olmalıdır!')
    .max(11, 'TCKN en fazla 11 karakter olmalıdır!')
    .tckn('Geçersiz TCKN, lütfen kontrol edin.')
});

// NAME-SURNAME Schema
const nameSurnameSchema = Yup.object({
  name: Yup.string()
    .min(2, 'Ad en az iki harften oluşmalı')
    .max(50, 'Ad en fazla 50 karakter olmalıdır!')
    .strict(false)
    .trim()
    .required(requiredText),
  surname: Yup.string()
    .min(2, 'Soyad en az iki harften oluşmalı')
    .max(50, 'Soyad en fazla 50 karakter olmalıdır!')
    .strict(false)
    .trim()
    .required(requiredText)
});

// BIRTDATE Schema
const requiredBirthdateSchema = Yup.object().shape({
  birthdate: Yup.string('Hatalı format').nullable().required(requiredText)
});

// TITLE - ROLE Schema
const titleSchema = Yup.object().shape({
  title: requiredString.strict(false).trim().min(2, 'Görev bilgisi en az 2 karakter olmalıdır!')
});

/* ------------------------------------------- FORM VALIDATION SCHEMAS -------------------------------------------------------------------- */

// Profile Update
export const updateProfileSchema = merge(nameSurnameSchema, requiredBirthdateSchema);

// Change Password => update profile(password) and initial login change password
export const changePasswordSchema = merge(passwordSchema);

// System User Add-Edit Schema
export const systemUserCrudSchema = isEdit =>
  merge(
    nameSurnameSchema,
    tcknSchema,
    phoneSchema,
    requiredEmailSchema,
    requiredBirthdateSchema,
    titleSchema,
    Yup.object().shape({
      role_id: Yup.string().required(requiredText)
    }),
    !isEdit && passwordSchema
  );

// Campaign Add-Edit Schema
export const campaignCrudSchema = isEdit =>
  merge(
    Yup.object().shape({
      name: Yup.string()
        .required(requiredText)
        .strict(false)
        .trim()
        .min(3, 'Kampanya adı en az 3 karakter olmalıdır!'),
      description: Yup.string()
        .required(requiredText)
        .min(3, 'Açıklama alanı en az 3 karakter olmalıdır!'),
      start_date: Yup.string('Hatalı format').nullable().required(requiredText),
      end_date: Yup.string('Hatalı format').nullable().required(requiredText),
      ...(!isEdit
        ? { content: Yup.string().required('Kampanya görseli alanı zorunludur.').nullable() }
        : { content: Yup.string().optional() })
    })
  );

// Pharmacy Add-Edit Schema
export const pharmacyCrudSchema = merge(
  Yup.object().shape({
    name: Yup.string()
      .required(requiredText)
      .strict(false)
      .trim()
      .min(2, 'Eczane adı en az 2 karakter olmalıdır!'),
    attendant: Yup.string()
      .required(requiredText)
      .nullable()
      .min(2, 'Yetkili adı en az 2 karakter olmalıdır!'),
    telephone: Yup.string()
      .required(requiredText)
      .min(10, 'Eksik ya da hatalı telefon numarası!')
      .nullable(),
    attendant_telephone: Yup.string()
      .required(requiredText)
      .nullable()
      .min(10, 'Eksik ya da hatalı telefon numarası!'),
    second_telephone: Yup.string()
      .optional()
      .nullable()
      .min(10, 'Eksik ya da hatalı telefon numarası!'),
    city_id: Yup.object().required(requiredText).nullable(),
    district_id: Yup.object().required(requiredText).nullable(),
    region_id: Yup.object().required(requiredText).nullable(),
    address: Yup.string().optional().min(2, 'Adres bilgisi en az 2 karakter olmalıdır!').nullable()
  }),
  optionalEmailSchema
);

// Pharmacy User Add-Edit Schema
export const pharmacyUserCrudSchema = isEdit =>
  merge(
    requiredEmailSchema,
    !isEdit && passwordSchema,
    Yup.object().shape({
      full_name: Yup.string()
        .required(requiredText)
        .strict(false)
        .trim()
        .min(2, 'Ad soyad en az 2 karakter olmalıdır!'),
      telephone: Yup.string()
        .required(requiredText)
        .min(10, 'Eksik ya da hatalı telefon numarası!')
        .nullable(),
      role_id: Yup.string().required(requiredText)
    })
  );
